/* eslint-disable react-hooks/exhaustive-deps */
import {
  FunctionComponent as F,
  Fragment,
  useEffect,
  useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ProtocolTable from '../components/Protocol/ProtocolTable';
import {
  deleteDosingsFromProtocolAsync,
  retrieveDosingsAsync,
  retrieveProtocolInfosAsync,
  retrievePublicProtocolAsync as retrievePublicProtocolWithDosingsAsync,
  shareProtocolAsync,
  updateCurrencyAsync,
  updateNumericValueAsync} from '../core/store/protocol/protocol.thunks';
import { useAppDispatch } from '../core/store/hooks';
import { IState } from '../core/store/store';
import {
  Box,
  Stack,
  Typography,
  Grid,
  colors,
  Link } from '@mui/material';
import Container from '../components/UI/Container/Container';
import PageHeadline from '../components/UI/Text/PageHeadline';
import Button from '../components/UI/Button/Button';
import { HOME, LOGIN, NEW, SAVE_PUBLIC_PROTOCOL } from '../core/router/paths';
import loadingGif from '../images/loading.gif';
import Drawer from '../components/Drawer/Drawer';
import { updateSavePublicProtocolId } from '../core/store/protocol/protocol.slice';
import Budget from '../components/Protocol/Budget';
import LoadingGifSmall from '../components/UI/Icons/LoadingGif/LoadingGifSmall';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ProtocolOrderReminder from '../components/Protocol/ProtocolOrderReminder';
import InvalidDosesDialog from '../components/Protocol/InvalidDosesDialog';
import TextTooltip from '../components/UI/Tooltip/TextTooltip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { urlWithProtocolId } from '../core/router/updatePath';
import { retrieveFoldersAsync } from '../core/store/protocol/protocolFolder.thunks';
import CurrencyDropdown from '../components/Listings/CurrencyDropdown';
import { IOption, ProtocolDosingRow } from '@protoplan/types';


enum SaveStatus
{
  SAVING,
  SAVED,
  ERROR
}

interface Params
{
  protocolId?: string
}

const Protocol: F = () =>
{
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams() as Params;

  const user = useSelector((state: IState) => state.user);
  const folders = useSelector((state: IState) => state.protocol.folders);
  const protocols = useSelector((state: IState) => state.protocol.protocols);
  const protocol = useSelector((state: IState) =>
    state.protocol.protocols.find(p => p.protocolId === Number(params.protocolId)));
  const edit =
    protocol?.userId === user.userId ||
    protocol?.sharedWith.find(u => u.userId === user.userId)?.edit;
  const dosings = useSelector((state: IState) =>
    state.protocol.protocols.find(p => p.protocolId === Number(params.protocolId))?.dosings);
  const [dosingsRetrieved, setDosingsRetrieved] = useState(Boolean(dosings) || false);

  const [invalidDosesDialog, setInvalidDosesDialog] = useState(false);
  const invalidDoses = (protocol?.dosings || [])
    .filter(d => !(d.unitOptions.map(o => o.value).includes(d.doseUnitId)))
    .map(d => ({ ...d, firstOptionUnit: d.unitOptions[0]?.label }));

  useEffect(() =>
  {
    setInvalidDosesDialog(Boolean(invalidDoses.length));
  }, [invalidDoses.length]);

  const [saveStatus, setSaveStatus] = useState<SaveStatus | undefined>(undefined);

  useEffect(() => setDosingsRetrieved(false), [protocol?.protocolId]);

  useEffect(() =>
  {
    const retrieveProtocols = async() =>
    {
      const protocols0 = protocols.length ? protocols : await appDispatch(retrieveProtocolInfosAsync());

      const retrievedProtocols = protocols0 ? [...protocols0] : [];

      let publicProtocol;
      if(params.protocolId)
      {
        if(!retrievedProtocols.map(p => p.protocolId).includes(Number(params.protocolId)))
          publicProtocol = await appDispatch(retrievePublicProtocolWithDosingsAsync(Number(params.protocolId)));

        if(publicProtocol)
        {
          retrievedProtocols.push(publicProtocol);

          if(publicProtocol?.userId !== user.userId)
            dispatch(updateSavePublicProtocolId(publicProtocol?.protocolId));
        }
      }

      // Failed to retrieve protocols - user attempting to access protocol not available to them
      if(!retrievedProtocols.length)
      {
        console.log("navigate 1 - failed to retrieve protocols");
        return navigate(HOME);
      }

      const protocolsForId = retrievedProtocols.filter(
        p => p.protocolId === Number(params.protocolId) && (
          p.userId === user.userId ||
          p.sharedWith.filter(u => u.userId === user.userId).length ||
          p.public));

      // When no protocolId in route params or it does not match any of the retrieved protocols,
      // load user's first protocol
      if(!params.protocolId || !protocolsForId.length)
      {
        console.log("navigate 2 - URL does not specify or matching protocol not retrieved");

        return navigate(urlWithProtocolId(Number(retrievedProtocols.find(p => p.userId === user.userId)?.protocolId)));
      }

      let dosings0 = dosings;
      if(!dosings0)
      {
        const dosings = await appDispatch(retrieveDosingsAsync(Number(params.protocolId)));
        dosings0 = dosings;
      }

      setDosingsRetrieved(Boolean(dosings0));
    };

    const retrieveFolders = async() =>
    {
      if(!user.premium)
        return;

      folders.length ? folders : appDispatch(retrieveFoldersAsync());
    }

    retrieveFolders()
      .then(retrieveProtocols);

  }, [params.protocolId]);

  const [rowsSelected, setRowsSelected] = useState(dosings ? !dosings.every(row => !row.checked) : false);
  useEffect(() =>
  {
    setRowsSelected(dosings ? !dosings.every(row => !row.checked) : false);
  }, [dosings]);

  if(!protocol)
    return <></>;

  const updateValue = async(dosingId: number, value: number, label: keyof ProtocolDosingRow) =>
  {
    setSaveStatus(SaveStatus.SAVING);
    const result = await appDispatch(updateNumericValueAsync(protocol.protocolId, dosingId, label, value));
    setSaveStatus(result?.success ? SaveStatus.SAVED : SaveStatus.ERROR);
  }

  const handleCloseInvalidDosesDialog = async() =>
  {
    await Promise.all(invalidDoses.map(async d =>
    {
      await updateValue(d.dosingId, d.recDoseUnitId || d.amountUnitId || d.unitOptions[0].value, "doseUnitId");
      await updateValue(d.dosingId, d.recDose ? d.recDose : 1, "dose");
    }));

    setInvalidDosesDialog(false);
  }

  const handleChangeCurrencyOption = (option: IOption | null) =>
  {
    if(!option)
      return;

    const currencySymbol = option.label.split('(')[0].trim();
    const currencyCode = option.label.match(/\(\w*\)/) ?
      (option.label.match(/\((\w*)\)/) as RegExpMatchArray)[1] :
      option.label;
    appDispatch(updateCurrencyAsync(Number(params.protocolId), option?.value, currencyCode, currencySymbol));
  }

  return (
    <Stack direction='row'>

      <Drawer/>

      <Container width='100%'>
        <Stack spacing={2} sx={{ width: '1160px' }}>
          <Grid container direction='row'>
            <Grid item style={{ display: "flex", justifyContent: "flex-start", flexGrow: 1 }}>
              <PageHeadline>{protocol.name}</PageHeadline>
            </Grid>
            <Grid item style={{ display: "flex", justifyContent: "flex-end" }}>
              <Stack direction={'column'} >
                <Box sx={{ height: 56 }}/>
                <Stack direction='row' spacing={3}>
                  <CurrencyDropdown
                    initialValue={protocol.currencyId}
                    onChangeOption={handleChangeCurrencyOption}
                    disabled={!edit}/>
                  {(protocol.protocolId && protocol?.userId === user.userId && user.premium) ?
                    <ProtocolOrderReminder protocolId={protocol.protocolId} /> :
                    ((protocol.protocolId && protocol?.userId === user.userId) ?
                      <TextTooltip
                        lines={["Support Protoplan on Patreon to turn on order reminders for this protocol"]}
                        tooltipPlacement='top-end'>
                        <Link
                          variant='body2'
                          target='_blank'
                          rel='noreferrer'
                          href={"https://www.patreon.com/Protoplan/"} >
                          <NotificationsIcon fontSize='large' sx={{ color: 'lightGray', cursor: 'pointer' }}/>
                        </Link>
                      </TextTooltip> :
                      <></>)}
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          {(dosingsRetrieved && params.protocolId) ?
            <ProtocolTable protocolId={Number(params.protocolId)} updateValue={updateValue}/> :
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img src={loadingGif} width='100px'/>
            </Box>}

          {invalidDosesDialog ? <InvalidDosesDialog
            dosings={invalidDoses}
            close={handleCloseInvalidDosesDialog} /> : <></>}

          <Box width='1160px'>
            <Box sx={{ display: 'inline-flex', float: 'left' }}>
              <Stack direction="row">
                {(rowsSelected && edit) ?
                  <Button
                    onClick={() => dispatch(deleteDosingsFromProtocolAsync(Number(params.protocolId)))}
                    label='Remove'
                    size='large'/> :
                  <Fragment />}
                <Box width={15}/>
                <Stack direction='row' sx={{ pt: 0.4 }}>
                  {(() => {
                    switch(saveStatus)
                    {
                      case SaveStatus.SAVING:
                        return <LoadingGifSmall />
                      case SaveStatus.SAVED:
                        return (
                          <>
                            <CheckCircleIcon fontSize='small' sx={{ color: colors.teal[500], opacity: 0.5 }}/>
                            <Box width={5}/>
                            <Typography variant='body2' sx={{ color: 'gray', opacity: 0.5 }}>
                              Saved
                            </Typography>
                          </>);
                      case SaveStatus.ERROR:
                        return (
                          <>
                            <ErrorIcon fontSize='small' sx={{ color: colors.red[100], opacity: 0.5 }}/>
                            <Box width={5}/>
                            <Typography variant='body2' sx={{ color: 'gray', opacity: 0.5 }}>
                              Error saving
                            </Typography>
                          </>);
                      default:
                        return <></>;
                    }
                  })()}
                </Stack>
                </Stack>
            </Box>
            <Box sx={{ display: 'inline-flex', float: 'right', width: '125px' }}>
              <Budget/>
            </Box>
            {user.username ?
              ((protocol.sharedWith
                  .filter(u => u.userId === user.userId).length || protocol.userId === user.userId) ?
                <></> :
                <Button
                 id='SaveShared'
                 label="Save to 'Shared with me' 💾"
                 onClick={() => user.userId && appDispatch(shareProtocolAsync(protocol.protocolId, user.userId))}/>) :
              (protocol.public ?
                <Button
                  id='LogInSaveProtocol'
                  label='Log in to save protocol 💾'
                  onClick={() => navigate(LOGIN + "/" + SAVE_PUBLIC_PROTOCOL, { relative: 'path' })}/> :
                <Button
                  id='SaveProtocol'
                  label='Save protocol 💾'
                  onClick={() => navigate(LOGIN + "/" + NEW, { relative: 'path' })}/>)}
          </Box>

        </Stack>
      </Container>

    </Stack>);
};

export default Protocol;
